<template>
  <section class="hero-section">
    <div class="section__container">
      <!-- HERO COPY -->
      <div class="hero-content">
        <h1 class="hero-title">
          Build <span class="underline">your product</span> the right way.
        </h1>

        <p class="hero-kicker">
          We deliver results. Launch your product faster. Scale with confidence.
        </p>

        <p class="hero-description">
          With DIGU’s service packages, you get proven frameworks that support
          every stage of your journey — from your first app to your first
          customers and beyond.
        </p>
      </div>

      <!-- SERVICE PACKAGES -->
      <div class="solutions-section">
        <!-- Shared viewport: header + slider share the same exact width -->
        <div class="solutions-viewport" :style="{ width: `${sliderWidth}px` }">
          <div class="solutions-header">
            <h2 class="solutions-title">
              Our <span class="accent">Service Packages</span>
            </h2>
            <div class="navigation-controls">
              <button
                class="nav-arrow"
                @click="previousSlide"
                :disabled="currentSlide === 0"
                aria-label="Previous"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </button>
              <button
                class="nav-arrow"
                @click="nextSlide"
                :disabled="currentSlide >= maxSlides"
                aria-label="Next"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </button>
            </div>
          </div>

          <div
            class="solutions-slider"
            :style="{ width: `${sliderWidth}px` }"
            @touchstart="onTouchStart"
            @touchmove="onTouchMove"
            @touchend="onTouchEnd"
          >
            <div class="solutions-track" :style="sliderStyle">
              <!-- Each column contains TWO stacked cards that move together -->
              <div
                v-for="(col, cIndex) in packageColumns"
                :key="cIndex"
                class="solutions-column"
                :style="{ width: `${cardWidth}px` }"
              >
                <div
                  v-for="(pkg, i) in col"
                  :key="pkg?.title || `empty-${i}`"
                  class="solution-card"
                  v-show="pkg"
                >
                  <h3 class="card-title">{{ pkg.title }}</h3>
                  <!-- <p class="card-charge">{{ pkg.charge }}</p> -->
                  <p class="card-description">{{ pkg.description }}</p>
                  <a v-if="hasLink(pkg.link)" :href="pkg.link" class="read-more">
                    Read more
                    <v-icon size="small">mdi-arrow-right</v-icon>
                  </a>
                </div>

                <!-- If odd count, pad with an invisible card to keep height/alignment -->
                <div
                  v-if="col.length === 1"
                  class="solution-card placeholder-card"
                ></div>
              </div>
            </div>
          </div>

          <!-- Indicators aligned to the same viewport width -->
          <div class="slider-indicators">
            <div
              v-for="(_, index) in packageColumns"
              :key="index"
              class="indicator"
              :class="{
                active: index >= currentSlide && index < currentSlide + visibleColumns,
                current: index === currentSlide,
              }"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';

const currentSlide = ref(0);
const cardWidth = ref(380); // width of ONE column (two stacked cards)
const gapWidth = 24;
const visibleColumns = ref(3); // show 3 columns (each has 2 cards) on desktop
const sliderWidth = ref(0);

// Touch state
const touchStartX = ref(0);
const touchEndX = ref(0);
const isSwiping = ref(false);

const packages = ref([
  {
    title: 'Web & Mobile UX/UI Design Package',
    charge: 'Starting at $7,999',
    description: 'Intuitive, accessible designs that bring your ideas to life',
    link: '/service-packages/custom-web-mobile-ux-ui-design',
  },
  {
    title: 'Business Website Package',
    charge: 'Starting at $7,999',
    description: 'Fast, SEO-ready websites that build trust and credibility.',
    link: '/service-packages/business-website-package',
  },
  {
    title: 'Marketing Website Package',
    charge: 'Starting at $9,999',
    description: 'Conversion-driven sites designed to capture leads and sales.',
    link: '/service-packages/marketing-website-package',
  },
  {
    title: 'Portfolio Website Package',
    charge: 'Starting at $5,999',
    description:
      'Sleek, SEO-friendly portfolios that spotlight your expertise.',
    link: '/service-packages/portfolio-website-package',
  },
  {
    title: 'Shopify Store Development Package',
    charge: 'Starting at $19,999',
    description: 'Launch Shopify-ready stores with secure, modern storefronts.',
    link: '/service-packages/shopify-store-development',
  },
  {
    title: 'Shopify Migration Package',
    charge: 'Starting at $19,999',
    description: 'Seamless migration to Shopify with SEO and data preserved',
    link: '/service-packages/shopify-migration',
  },
  {
    title: 'Custom E-Commerce Website Development',
    charge: 'Starting at $79,999',
    description:
      'Custom platforms built for complex workflows and integrations.',
    link: '/service-packages/custom-ecommerce-development',
  },
  {
    title: 'Stripe & Payment Integration',
    charge: 'Starting at $5,999',
    description:
      'Secure Stripe integration for one-time and recurring payments.',
    link: '/service-packages/stripe-and-payment-integration',
  },
  {
    title: 'API & 3rd-Party Tool Integrations ',
    charge: 'Starting at $7,999',
    description: 'Seamless API integrations for faster, connected workflows.',
    link: '/service-packages/api-and-3p-integrations',
  },
  {
    title: 'AI Code Review & Hardening ',
    charge: 'Starting at $7,999',
    description: 'AI code refactored to secure, production-ready standards.',
    link: '/service-packages/ai-code-review-hardening',
  },
  {
    title: 'Progressive Web App (PWA) Conversion (Basic)',
    charge: 'Starting at $13,999',
    description:
      'Websites converted into fast, mobile-like apps with PWA tech.',
    link: '/service-packages/pwa-conversion-basic',
  },
  {
    title: 'Progressive Web App (PWA) Conversion with Database',
    charge: 'Starting at $17,999',
    description: 'Scalable PWAs with database support for advanced features.',
    link: '/service-packages/pwa-conversion-with-database',
  },
]);

const hasLink = (val) =>
  typeof val === 'string' ? val.trim().length > 0 : Boolean(val);

/** Group into columns of two (top/bottom) so they move together */
const packageColumns = computed(() => {
  const cols = [];
  for (let i = 0; i < packages.value.length; i += 2) {
    const pair = [packages.value[i]];
    if (packages.value[i + 1]) pair.push(packages.value[i + 1]);
    cols.push(pair);
  }
  return cols;
});

/** translate by column width so pairs move together */
const sliderStyle = computed(() => ({
  transform: `translateX(${
    -currentSlide.value * (cardWidth.value + gapWidth)
  }px)`,
}));

/** Max slide based on columns, not individual cards */
const maxSlides = computed(() =>
  Math.max(0, packageColumns.value.length - visibleColumns.value)
);

/** Responsive sizing: keep your breakpoints, but now counting columns */
const updateSliderDimensions = () => {
  const screenWidth = window.innerWidth;
  const containerPadding = 48;

  if (screenWidth <= 450) {
    visibleColumns.value = 1;
    cardWidth.value = screenWidth - containerPadding - 30;
    sliderWidth.value = cardWidth.value;
  } else if (screenWidth <= 800) {
    visibleColumns.value = 1;
    cardWidth.value = Math.min(380, screenWidth - containerPadding);
    sliderWidth.value = cardWidth.value;
  } else if (screenWidth <= 1200) {
    visibleColumns.value = 2;
    cardWidth.value = Math.min(350, (screenWidth - 96) / 2);
    sliderWidth.value = cardWidth.value * 2 + gapWidth;
  } else {
    visibleColumns.value = 3;
    cardWidth.value = 380;
    sliderWidth.value = cardWidth.value * 3 + gapWidth * 2;
  }

  if (currentSlide.value > maxSlides.value) {
    currentSlide.value = maxSlides.value;
  }
};

// arrows
const nextSlide = () => {
  if (currentSlide.value < maxSlides.value) currentSlide.value++;
};
const previousSlide = () => {
  if (currentSlide.value > 0) currentSlide.value--;
};

// touch swipe
const onTouchStart = (e) => {
  touchStartX.value = e.touches[0].clientX;
  isSwiping.value = true;
};
const onTouchMove = (e) => {
  if (!isSwiping.value) return;
  touchEndX.value = e.touches[0].clientX;
};
const onTouchEnd = () => {
  if (!isSwiping.value) return;
  const deltaX = touchEndX.value - touchStartX.value;
  const swipeThreshold = 50;
  if (deltaX > swipeThreshold) previousSlide();
  else if (deltaX < -swipeThreshold) nextSlide();
  touchStartX.value = 0;
  touchEndX.value = 0;
  isSwiping.value = false;
};

onMounted(() => {
  updateSliderDimensions();
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(updateSliderDimensions, 250);
  });
});
</script>

<style scoped>
/* Background + spacing */
.hero-section {
  position: relative;
  background: linear-gradient(120deg, #fbe9e7 0%, #e3f2fd 100%);
  padding: 6rem 0;
}
.section__container {
  max-width: 1500px;
  margin: 0 auto;
}

/* Headline + kicker */
.hero-content {
  text-align: center;
}
.hero-title {
  font-size: 3.5rem;
  line-height: 1.2;
  margin-bottom: 1.25rem;
  color: #111827;
}
.underline {
  position: relative;
  display: inline-block;
}
.underline::after {
  content: '';
  position: absolute;
  left: 100%;
  bottom: -6px;
  width: 100%;
  height: 10px;
  background-image: url('@/assets/img/underline.svg');
  background-repeat: no-repeat;
  background-size: contain;
  transform: translateX(-50%);
}
.hero-kicker {
  font-weight: 600;
  font-size: 1.25rem;
  color: #ef4444;
  margin-bottom: 0.75rem;
}
.hero-description {
  margin: 0 10%;
  font-size: 1.125rem;
  line-height: 1.6;
  color: #4a4a4a;
  margin-bottom: 3.5rem;
}

/* Packages */
.solutions-section {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
}

/* NEW: shared viewport that constrains header + rail to the same width */
.solutions-viewport {
  margin: 0 auto;
  /* width is set inline via :style using sliderWidth */
}

.solutions-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 1.5rem 0; /* no side padding so it matches the rail width */
  width: 100%;          /* fill the viewport width exactly */
  box-sizing: border-box;
}

.solutions-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
}
.solutions-title .accent {
  color: #ef4444;
}
.navigation-controls {
  display: flex;
  gap: 1rem;
}
.nav-arrow {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
.nav-arrow:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Slider constrained to viewport width */
.solutions-slider {
  overflow: hidden;
  margin: 0 auto;
  width: 100%;
}

/* The horizontal track now contains VERTICAL columns (each with two cards) */
.solutions-track {
  display: flex;
  gap: 24px;
  transition: transform 0.3s ease-out;
  align-items: stretch;
}

/* A column is a vertical stack of two cards that move together */
.solutions-column {
  display: grid;
  grid-auto-rows: 1fr;
  row-gap: 24px;
  flex-shrink: 0;
}

/* Card */
.solution-card {
  background: #fff;
  border-radius: 16px;
  padding: 1rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 160px;
}
.placeholder-card {
  visibility: hidden;
}

.card-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 0.75rem;
  text-align: left;
}
.card-charge {
  text-align: left;
  font-weight: 700;
}
.card-description {
  font-size: 1rem;
  line-height: 1.5;
  color: #4a4a4a;
  margin-bottom: 0.5rem;
  text-align: left;
}
.read-more {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #2196f3;
  text-decoration: none;
  font-weight: 500;
}

/* Indicators aligned to the same viewport width */
.slider-indicators {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 1.25rem;
  width: 100%; /* matches viewport width */
}
.indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e0e0e0;
  transition: all 0.3s ease;
}
.indicator.active {
  background: #2196f3;
}
.indicator.current {
  width: 24px;
  border-radius: 4px;
}

/* Responsive tweaks */
@media (max-width: 1200px) {
  .hero-title {
    font-size: 2.5rem;
  }
  .hero-description {
    margin: 0 5% 3rem;
  }
  .solutions-title {
    font-size: 1.8rem;
  }
}
@media (max-width: 800px) {
  .hero-title {
    font-size: 2rem;
  }
  .hero-description {
    font-size: 1rem;
    margin: 0 0 2.5rem;
  }
  .section__container {
    padding: 0;
  }
  .solutions-title {
    font-size: 1.6rem;
  }
}
@media (max-width: 450px) {
  .section__container {
    padding: 0;
  }
  .solutions-title {
    font-size: 1.2rem;
  }
  .solution-card {
    padding: 0.75rem;
  }
  .card-title {
    font-size: 1.1rem;
  }
  .card-description {
    font-size: 0.9rem;
  }
}
</style>
